import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import CreatableSelect from 'react-select/lib/Creatable';
import { components } from 'react-select/lib/components';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import contributorTheme from 'theme/contributor.theme';
import appTheme from 'theme/app.theme';

const { palette, typography, zIndex } = contributorTheme;

const getTheme = theme => ({
  ...theme,
  borderRadius: 0,
  colors: {
    ...theme.colors,
    danger: palette.secondary.main,
    dangerLight: 'transparent',
    neutral10: palette.primary.main,
    neutral50: palette.text.secondary,
    primary: palette.action.selected,
    primary25: palette.action.hover,
    primary50: palette.action.hover,
  },
});

const styles = {
  control: base => ({
    ...base,
    backgroundColor: 'transparent',
    borderStyle: 'none',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    boxShadow: 'none',
  }),
  singleValue: base => ({
    ...base,
    color: palette.text.primary,
    fontFamily: typography.fontFamily,
  }),
  menu: base => ({
    ...base,
    zIndex: zIndex.appBar + 1,
  }),
  menuList: base => ({
    ...base,
    color: appTheme.palette.text.primary,
  }),
  multiValue: base => ({
    ...base,
    alignItems: 'center',
    borderRadius: 16,
    height: 32,
    margin: 4,
    paddingLeft: 8,
    paddingRight: 8,
    boxShadow: appTheme.shadows[1],
  }),
  multiValueRemove: base => ({
    ...base,
    color: palette.primary.light,
  }),
  multiValueLabel: base => ({
    ...base,
    ...typography.body2,
    color: palette.primary.light,
    fontSize: 13,
  }),
  noOptionsMessage: base => ({
    ...base,
    ...typography.body2,
    color: palette.text.secondary,
  }),
  option: (base, state) => ({
    ...typography.body1,
    ...base,
    color: state.isDisabled
      ? palette.text.disabled
      : state.isSelected
      ? palette.text.primary
      : appTheme.palette.text.primary,
  }),
  placeholder: base => ({
    ...typography.body1,
    ...base,
    fontSize: 16,
  }),
  valueContainer: base => ({
    ...base,
    paddingLeft: 0,
    paddingRight: 0,
  }),
};

const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <ArrowDropDown />
  </components.DropdownIndicator>
);

const IndicatorSeparator = () => <div />;

const MultiValueRemove = props => (
  <components.MultiValueRemove {...props}>
    <HighlightOffIcon />
  </components.MultiValueRemove>
);

const customComponents = {
  DropdownIndicator,
  IndicatorSeparator,
  MultiValueRemove,
};

class ReactSelect extends React.Component {
  state = {
    focused: false,
  };

  handleBlur = e => {
    this.setState({ focused: false });
    this.props.formik.handleBlur(e);
  };

  handleChange = newValues => {
    const {
      formik,
      selectProps: { isMulti, name },
    } = this.props;
    formik.setFieldValue(name, isMulti ? newValues : [newValues]);
  };

  handleFocus = () => this.setState({ focused: true });

  defaultSelectProps = {
    isClearable: false,
    onBlur: this.handleBlur,
    onChange: this.handleChange,
    onFocus: this.handleFocus,
    components: customComponents,
    styles: styles,
    theme: getTheme,
  };

  render() {
    const {
      createable,
      helperText,
      label,
      selectProps,
      ...otherProps
    } = this.props;

    const mergedSelectProps = {
      ...this.defaultSelectProps,
      ...selectProps,
      'aria-labelledby': `${selectProps.name}-label`,
      placeholder: this.state.focused
        ? selectProps.placeholder || 'Select…'
        : '',
    };

    return (
      <FormControl {...otherProps}>
        <InputLabel
          id={`${selectProps.name}-label`}
          focused={this.state.focused}
          shrink={this.state.focused || selectProps.value.length > 0}
        >
          {label}
        </InputLabel>
        {createable ? (
          <CreatableSelect {...mergedSelectProps} />
        ) : (
          <Select {...mergedSelectProps} />
        )}
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    );
  }
}

ReactSelect.propTypes = {
  createable: PropTypes.bool,
  formik: PropTypes.shape({
    handleBlur: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
  }),
  helperText: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  selectProps: PropTypes.shape({
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired,
      })
    ),
    value: PropTypes.array.isRequired,
  }).isRequired,
};

export default ReactSelect;
